<script lang="ts">
  import { addCredits } from '../lib/components/map/credits';
  import { onMount, onDestroy } from 'svelte';
  import { navigate } from 'svelte-routing';
  import * as maplibregl from 'maplibre-gl';
  import { api } from '../lib/api/client';
  import type { Fix, LiveJoined, MapInfo, SosAlert } from '../lib/api/types';
  import { isAuthenticated } from '../lib/stores/auth';
  import { websocket } from '../lib/stores/websocket';
  import { age, when } from '../lib/utils/time';
  import { initials } from '../lib/utils/geo';
  import { wakeCause } from '../lib/utils/memberState';
  import { watchSight } from '../lib/utils/sight';
  import { followTheme, northUpMap, styleFor } from '../lib/components/map/theme';
  import { integer, t } from '../lib/i18n/index.ts';

  // Sos shows an SOS (spec 004). A person it paged sees where the member
  // is, the path since the SOS, a way to call them, and live following,
  // which starts on its own while it is open (004 FR-017, 004 FR-019). The member
  // sees whom it went to and marks themselves safe (004 FR-021).
  let { id }: { id: string } = $props();

  let sos = $state<SosAlert | null>(null);
  let error = $state('');
  let marking = $state(false);
  let liveState = $state('');
  let liveReason = $state('');
  let mapEl: HTMLDivElement | undefined = $state();
  let map: maplibregl.Map | null = null;
  let stopTheme: (() => void) | null = null;
  let marker: maplibregl.Marker | null = null;
  let path: [number, number][] = [];
  // latest is the newest fix the page has, from the path or live following.
  let latest = $state<Fix | null>(null);
  let session = '';
  let unsubscribe: (() => void) | null = null;
  let rejoin: ReturnType<typeof setInterval> | null = null;
  let centering = true;
  const mine = $derived(sos !== null && sos.recipients !== undefined);
  const stopWatchingSight = watchSight(
    () => {
      unfollow();
      liveState = 'away';
    },
    () => {
      if (canFollow()) follow(sos!.group_id!, sos!.user_id);
    },
  );

  $effect(() => {
    if (!$isAuthenticated) navigate('/login?next=/sos/' + id, { replace: true });
  });

  function canFollow(): boolean {
    return !!sos && !mine && sos.state === 'open' && !!sos.group_id;
  }

  onMount(async () => {
    try {
      sos = await api.get<SosAlert>(`/sos/${id}`);
    } catch (e: any) {
      error = e.message;
      return;
    }
    const fixes = await api.get<Fix[]>(`/sos/${id}/path`).catch(() => [] as Fix[]);
    path = fixes.map((f) => [f.lon, f.lat]);
    latest = fixes.length ? fixes[fixes.length - 1] : null;
    info = await api.get<MapInfo>('/map').catch(() => null);
    const at: [number, number] | null = latest ? [latest.lon, latest.lat] : sos.lat != null && sos.lon != null ? [sos.lon, sos.lat] : null;
    if (at) show(at);
    // An SOS sent without a location is still followed; the map appears
    // with the first fix live following brings.
    if (canFollow()) follow(sos.group_id!, sos.user_id);
  });

  let info: MapInfo | null = null;

  // show moves the member's marker, making the map on the first location.
  function show(at: [number, number]) {
    if (marker) {
      marker.setLngLat(at);
      if (centering) map?.easeTo({ center: at, duration: 500 });
      return;
    }
    if (!mapEl || !sos) return;
    map = northUpMap({ container: mapEl, style: styleFor(info), center: at, zoom: 15, attributionControl: false });
    addCredits(map, info, 'other');
    const el = document.createElement('div');
    el.className = 'sos-marker';
    el.textContent = initials(sos.display_name);
    marker = new maplibregl.Marker({ element: el }).setLngLat(at).addTo(map);
    stopTheme = followTheme(map, info, drawPath);
    map.on('dragstart', (e) => {
      if (e.originalEvent) centering = false;
    });
  }

  onDestroy(() => {
    stopTheme?.();
    stopWatchingSight();
    unfollow();
    map?.remove();
  });

  // drawPath draws the path since the SOS, again after a theme change
  // replaces the style.
  function drawPath() {
    if (!map || !map.isStyleLoaded()) return;
    const data: GeoJSON.Feature = { type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: path } };
    const src = map.getSource('sos-path') as maplibregl.GeoJSONSource | undefined;
    if (src) {
      src.setData(data);
      return;
    }
    map.addSource('sos-path', { type: 'geojson', data });
    map.addLayer({
      id: 'sos-path',
      type: 'line',
      source: 'sos-path',
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      paint: { 'line-color': getComputedStyle(document.documentElement).getPropertyValue('--theme-error').trim() || '#d20f39', 'line-width': 4, 'line-opacity': 0.8 },
    });
  }

  let generation = 0;

  function unfollow(): Promise<void> {
    generation++;
    unsubscribe?.();
    unsubscribe = null;
    if (rejoin) clearInterval(rejoin);
    rejoin = null;
    const left = session ? api.delete(`/live/${session}/viewers/me`).then(() => {}, () => {}) : Promise.resolve();
    session = '';
    return left;
  }

  async function follow(groupId: string, userId: string) {
    try {
      await join(groupId, userId);
      rejoin = setInterval(() => join(groupId, userId).catch(() => {}), 60_000);
    } catch {
      liveState = '';
    }
  }

  // join joins the member's live session and subscribes to it, again every
  // minute and after the socket reconnects (002 FR-014, 002 FR-015).
  async function join(groupId: string, userId: string) {
    const gen = generation;
    const j = await api.post<LiveJoined>(`/groups/${groupId}/members/${userId}/live`);
    if (gen !== generation) return;
    if (!liveState || j.session_id !== session) {
      liveState = j.state;
      liveReason = j.reason ?? '';
    }
    session = j.session_id;
    unsubscribe?.();
    unsubscribe = websocket.subscribeTopic(j.topic, (payload, _t, type) => {
      if (type === 'point') {
        const f = payload as Fix;
        latest = f;
        path = [...path, [f.lon, f.lat]];
        drawPath();
        show([f.lon, f.lat]);
        liveState = 'streaming';
      } else if (type === 'state') {
        const st = payload as { state: string; reason?: string };
        liveState = st.state;
        liveReason = st.reason ?? '';
        // An ended session, as after "I'm safe", is not joined again; the
        // alert is read afresh instead.
        if (st.state === 'ended') {
          unfollow();
          liveState = 'ended';
          api.get<SosAlert>(`/sos/${id}`).then((a) => (sos = a)).catch(() => {});
        }
      }
    });
  }

  let wasConnected = false;
  $effect(() => {
    const connected = $websocket.connected;
    if (connected && !wasConnected && session && sos?.group_id) join(sos.group_id, sos.user_id).catch(() => {});
    wasConnected = connected;
  });

  async function tryAgain() {
    if (!sos?.group_id) return;
    await unfollow();
    follow(sos.group_id, sos.user_id);
  }

  async function markSafe() {
    marking = true;
    error = '';
    try {
      sos = await api.post<SosAlert>(`/me/sos/${id}/safe`);
    } catch (e: any) {
      error = e.message;
    } finally {
      marking = false;
    }
  }

  // liveWords says how following goes; a state the page does not know shows
  // as the server named it.
  function liveWords(state: string, reason: string): string {
    switch (state) {
      case 'waiting': return t('sosLiveWaiting');
      case 'streaming': return t('sosLiveStreaming');
      case 'unreachable': return reason ? t('sosLiveUnreachableBecause', { cause: wakeCause(reason) }) : t('sosLiveUnreachable');
      case 'ended': return t('sosLiveEnded');
      case 'away': return t('sosLiveAway');
      default: return state;
    }
  }

  // where is the location the page shows: the newest fix, else the one the
  // SOS carried.
  const where = $derived(
    latest
      ? { at: latest.recorded_at, acc: latest.accuracy_m }
      : sos?.located_at
        ? { at: sos.located_at, acc: sos.accuracy_m ?? 0 }
        : null,
  );
</script>

<div class="flex flex-1 min-h-0">
  <aside class="w-96 shrink-0 border-r p-5 space-y-3" style="border-color: var(--theme-surface0); background: var(--theme-mantle);">
    {#if error && !sos}
      <p style="color: var(--theme-error);">{error}</p>
    {:else if sos}
      <h1 class="text-xl font-semibold" style={sos.state === 'open' ? 'color: var(--theme-error);' : ''}>
        {#if mine}
          {t('sosMineTitle', { state: sos.state })}
        {:else}{t('sosTitle', { state: sos.state, member: sos.display_name })}{/if}
      </h1>
      <p class="text-sm">{sos.late ? t('sosSentLate', { when: when(sos.sent_at), received: when(sos.received_at) }) : t('sosSent', { when: when(sos.sent_at) })}</p>
      {#if sos.state === 'safe' && sos.resolved_at}
        <p class="text-sm">{t('sosMarkedSafe', { when: when(sos.resolved_at) })}</p>
      {:else if sos.state === 'closed'}
        <p class="text-sm text-text-muted">{t('sosClosed')}</p>
      {/if}
      {#if where}
        <p class="text-sm">{latest
          ? t('sosLatest', { age: age(where.at), accuracy: integer(Math.round(where.acc)) })
          : t('sosWhenSent', { age: age(where.at), accuracy: integer(Math.round(where.acc)) })}</p>
      {:else}
        <p class="text-sm">{t('sosUnknown')}</p>
      {/if}
      {#if mine}
        <p class="text-sm">{t('sosSentTo', { people: sos.recipients?.map((r) => r.display_name).join(', ') ?? '' })}</p>
        {#if sos.state === 'open'}
          <p class="text-sm text-text-muted">{t('sosFollowing')}</p>
          <button class="btn btn-primary" onclick={markSafe} disabled={marking}>{t('sosSafe')}</button>
        {/if}
      {:else}
        {#if sos.phone}
          <a class="btn btn-primary inline-block" href={`tel:${sos.phone}`}><i aria-hidden="true" class="las la-phone mr-1"></i>{t('sosCall', { member: sos.display_name, phone: sos.phone })}</a>
        {:else}
          <p class="text-sm text-text-muted">{t('sosNoPhone', { member: sos.display_name })}</p>
        {/if}
        {#if liveState}
          <p class="text-sm text-text-muted">
            <i aria-hidden="true" class="las la-broadcast-tower mr-1"></i>{liveWords(liveState, liveReason)}
          </p>
          {#if liveState === 'unreachable'}
            <button class="btn btn-secondary btn-sm" onclick={tryAgain}>{t('appRetry')}</button>
          {/if}
        {/if}
      {/if}
      {#if error}<p class="text-sm" style="color: var(--theme-error);">{error}</p>{/if}
    {:else}
      <p class="text-text-muted">{t('sosLoading')}</p>
    {/if}
  </aside>
  <div class="flex-1 relative min-w-0">
    <div bind:this={mapEl} class="absolute inset-0"></div>
  </div>
</div>

<style>
  :global(.sos-marker) {
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 9999px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 0.8rem;
    background: var(--theme-error);
    color: var(--theme-base);
    border: 3px solid var(--theme-base);
    box-shadow: var(--theme-shadow-md);
  }
</style>
